import React, { useState } from 'react';
import { API_BASE } from '../apiBase';

interface BlogGenerationResult {
  success: boolean;
  url?: string;
  articleId?: string;
  error?: string;
  message?: string;
  images?: Array<{
    sectionNumber: number;
    heading: string;
    prompt: string;
    altText: string;
    shopifyUrl: string;
    productName?: string;
    productLink?: string;
  }>;
}

const BlogGenerator: React.FC = () => {
  const [blogContent, setBlogContent] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState<BlogGenerationResult | null>(null);
  const [progress, setProgress] = useState<string[]>([]);
  const [imagesOnly, setImagesOnly] = useState(false);
  const [generateImages, setGenerateImages] = useState(true);

  const handleGenerate = async () => {
    if (!blogContent.trim()) {
      alert('Please enter blog content');
      return;
    }

    setIsGenerating(true);
    setResult(null);
    setProgress(['🚀 Starting blog generation process...']);

    try {
      const response = await fetch(`${API_BASE}/api/generate-blog`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ content: blogContent, imagesOnly, generateImages }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseText = await response.text();
      if (!responseText || !responseText.trim()) {
        throw new Error('Empty response from blog generation API');
      }

      let data: BlogGenerationResult;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error('Blog generation API returned invalid JSON');
      }
      setResult(data);

      if (data.success) {
        setProgress(prev => [...prev, '✅ Blog generated and uploaded successfully!']);
      } else {
        setProgress(prev => [...prev, `❌ Error: ${data.error}`]);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setResult({ success: false, error: errorMessage });
      setProgress(prev => [...prev, `❌ Error: ${errorMessage}`]);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClear = () => {
    setBlogContent('');
    setResult(null);
    setProgress([]);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="bg-card rounded-xl shadow-sm border border-border p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-r from-primary to-primary/80 rounded-lg flex items-center justify-center">
            <span className="text-primary-foreground text-xl">📝</span>
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">AI Blog Generator</h2>
            <p className="text-sm text-muted-foreground">Generate complete blog posts with AI-generated images and product recommendations</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="blog-content" className="block text-sm font-medium text-foreground mb-2">
              Blog Content
            </label>
            <textarea
              id="blog-content"
              value={blogContent}
              onChange={(e) => setBlogContent(e.target.value)}
              placeholder={`Enter your blog content here. Include your title, sections, and content. 

Add external links instruction at the bottom in this format:

Sources and hyperlink placement:
* **Source:** https://example.com
* **Sentence from your blog post to hyperlink:** "Your exact sentence here"

* **Source:** https://another-example.com  
* **Sentence from your blog post to hyperlink:** "Another sentence to link"`}
              className="w-full h-96 px-4 py-3 bg-background border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-sm text-foreground placeholder:text-muted-foreground"
              disabled={isGenerating}
            />
            <p className="text-xs text-muted-foreground mt-2">
              The system will analyze your content, generate relevant images with Gemini AI, find matching products, and create a complete HTML blog post.
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleGenerate}
              disabled={isGenerating || !blogContent.trim()}
              className="flex-1 bg-gradient-to-r from-primary to-primary/80 text-primary-foreground px-6 py-3 rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-primary/25"
            >
              {isGenerating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Generating {imagesOnly ? 'Images' : 'Blog'}...
                </>
              ) : (
                <>
                  <span>🚀</span>
                  Generate {imagesOnly ? 'Images Only' : 'Blog Post'}
                </>
              )}
            </button>

            <button
              onClick={handleClear}
              disabled={isGenerating}
              className="px-6 py-3 border border-border text-foreground rounded-lg font-semibold hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              Clear
            </button>
          </div>

          {/* Generation Options */}
          <div className="space-y-3 pt-2 border-t border-border">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="generate-images"
                checked={generateImages}
                onChange={(e) => setGenerateImages(e.target.checked)}
                className="w-4 h-4 text-primary bg-background border-input rounded focus:ring-primary focus:ring-2"
                disabled={isGenerating}
              />
              <label htmlFor="generate-images" className="text-sm text-foreground">
                Generate AI Images (uncheck for text-only blog)
              </label>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="images-only"
                checked={imagesOnly}
                onChange={(e) => setImagesOnly(e.target.checked)}
                className="w-4 h-4 text-primary bg-background border-input rounded focus:ring-primary focus:ring-2"
                disabled={isGenerating || !generateImages}
              />
              <label htmlFor="images-only" className="text-sm text-foreground">
                Generate Images Only (no blog post)
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Display */}
      {progress.length > 0 && (
        <div className="bg-muted/50 rounded-xl border border-border p-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">Generation Progress</h3>
          <div className="space-y-1">
            {progress.map((step, index) => (
              <div key={index} className="text-sm text-muted-foreground font-mono">
                {step}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Results Display */}
      {result && (
        <div className={`rounded-xl border p-6 ${result.success
          ? 'bg-success/10 border-success/20'
          : 'bg-destructive/10 border-destructive/20'
          }`}>
          <div className="flex items-start gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${result.success ? 'bg-success' : 'bg-destructive'
              }`}>
              <span className="text-primary-foreground text-sm">
                {result.success ? '✓' : '✗'}
              </span>
            </div>
            <div className="flex-1">
              <h3 className={`font-semibold ${result.success ? 'text-success' : 'text-destructive'
                }`}>
                {result.success ? (imagesOnly ? 'Images Generated Successfully!' : 'Blog Generated Successfully!') : 'Generation Failed'}
              </h3>

              {result.success && result.message && (
                <p className="text-success/90 text-sm mt-2">{result.message}</p>
              )}

              {result.success && result.images && (
                <div className="mt-4 space-y-4">
                  <h4 className="text-sm font-semibold text-foreground">Generated Images:</h4>
                  <div className="space-y-3">
                    {result.images.map((image, index) => (
                      <div key={index} className="bg-card rounded-lg border border-border p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h5 className="font-medium text-foreground">Section {image.sectionNumber}: {image.heading}</h5>
                            <p className="text-sm text-muted-foreground mt-1"><strong>Prompt:</strong> {image.prompt}</p>
                            <p className="text-sm text-muted-foreground mt-1"><strong>Alt Text:</strong> {image.altText}</p>
                            {image.productName && (
                              <p className="text-sm text-muted-foreground mt-1"><strong>Product:</strong> {image.productName}</p>
                            )}
                          </div>
                          <div className="ml-4 text-right">
                            <a
                              href={image.shopifyUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-primary hover:text-primary/80 text-sm font-medium"
                            >
                              <span>🖼️</span>
                              View Image
                            </a>
                            {image.productLink && (
                              <div className="mt-1">
                                <a
                                  href={image.productLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-primary hover:text-primary/80 text-sm font-medium"
                                >
                                  <span>🛍️</span>
                                  Shop Product
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {result.success && !imagesOnly && result.url && (
                <div className="mt-2 space-y-3">
                  <p className="text-success/90 text-sm">{result.message}</p>
                  <div className="flex flex-wrap gap-3">
                    <a
                      href={result.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-primary hover:text-primary/80 text-sm font-medium"
                    >
                      <span>🔗</span>
                      View Blog Post in Shopify Admin
                    </a>
                    <button
                      onClick={async () => {
                        if (!confirm('Schedule this blog post to your Content Calendar?')) return;
                        try {
                          const res = await fetch(`${API_BASE}/api/calendar/posts/add`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            credentials: 'include',
                            body: JSON.stringify({
                              platform: 'shopify',
                              content: {
                                topic: blogContent.split('\n')[0]?.substring(0, 80) || 'New Blog Post',
                                caption: 'Generated by Blog Generator',
                                hashtags: ['blog', 'shopify']
                              },
                              type: 'blog_post',
                              url: result.url
                            })
                          });
                          const data = await res.json();
                          if (data.success) {
                            alert('✅ Blog post scheduled to calendar!');
                          } else {
                            alert('❌ Failed to schedule: ' + data.error);
                          }
                        } catch (e) {
                          alert('❌ Error scheduling blog post');
                        }
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors"
                    >
                      <span>📅</span>
                      Schedule to Calendar
                    </button>
                  </div>
                </div>
              )}

              {!result.success && result.error && (
                <p className="text-destructive text-sm mt-2">{result.error}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-primary/10 rounded-xl border border-primary/20 p-6">
        <h3 className="text-primary font-semibold mb-3 flex items-center gap-2">
          <span>💡</span>
          How to Use
        </h3>
        <div className="text-primary/80 text-sm space-y-2">
          <p><strong>1. Write your blog content:</strong> Include title, headings, and paragraphs. The AI will analyze and create sections automatically.</p>
          <p><strong>2. Add external links (optional):</strong> At the bottom, add "Sources and hyperlink placement:" followed by source URLs and exact sentences to hyperlink.</p>
          <p><strong>3. Choose generation mode:</strong> Check "Generate Images Only" to get just the AI-generated images with prompts and Shopify URLs, without creating a blog post. Uncheck to generate the full blog post.</p>
          <p><strong>4. Generate:</strong> The system will analyze sections, generate relevant images with Gemini AI, find matching products, and {imagesOnly ? 'return the image data' : 'create a complete HTML blog post'}.</p>
          <p><strong>5. Review:</strong> {imagesOnly ? 'Review the generated images with their prompts and download/view them from Shopify CDN.' : 'The blog will be automatically uploaded to your Shopify store and you\'ll get a link to review it.'}</p>
        </div>
      </div>
    </div>
  );
};

export default BlogGenerator;
